import { useCallback, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { MediaBtnContainer } from '../../../pre-meeting/components/Buttons'
import { MediaDevice } from '../../types/video-types'
import { StyledDropdownOptions } from '../Misc/StyledDropdownOptions'
import { Tip } from '../../../../components/Tip'
import { useUI } from '../../../../context/UIContext'
import { useZoomContext } from '../../../../context/ZoomContext'
import { useTranslation } from 'react-i18next'
import CloseFloatingBtn from '../../../../components/CloseFloatingBtn'
import { getDeviceType } from '../../util/platform'

export const CameraSelector = () => {
    const { t } = useTranslation('meeting')
    const [activeCamera, setActiveCamera] = useState<MediaDevice | undefined>(undefined)
    const [cameras, setCameras] = useState<MediaDevice[]>([])
    const { mediaStream, zoomClient } = useZoomContext()
    const { showToast } = useUI()
    const type: 'mobile' | 'desktop' = getDeviceType()
    const [isOpen, setIsOpen] = useState(false)

    const updateActiveCameraFromStream = useCallback(() => {
        if (!mediaStream) return
        const activeCameraId = mediaStream.getActiveCamera()
        const currentActiveCamera = cameras.find(cam => cam.deviceId === activeCameraId)
        if (currentActiveCamera) {
            setActiveCamera(currentActiveCamera)
        }
    }, [mediaStream, cameras])

    const getCameraList = useCallback(async () => {
        if (!mediaStream) return
        const cameraList = mediaStream.getCameraList()
        setCameras(cameraList)
    }, [mediaStream])

    useEffect(() => {
        if (!mediaStream) return
        getCameraList()
    }, [getCameraList, mediaStream])

    // Update active camera whenever camera list changes
    useEffect(() => {
        updateActiveCameraFromStream()
    }, [cameras, updateActiveCameraFromStream])

    useEffect(() => {
        zoomClient.on('device-change', getCameraList)
        return () => {
            zoomClient.off('device-change', getCameraList)
        }
    }, [getCameraList, zoomClient])

    const handleChevronClick = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsOpen(!isOpen)
    }

    const switchCamera = async (device: MediaDevice) => {
        if (!mediaStream) return
        const { deviceId, label } = device
        if (deviceId !== activeCamera?.deviceId) {
            await mediaStream.switchCamera(deviceId)
            setActiveCamera(device)
            showToast(t('microphone.selectedMicToast', { label }))
        }
    }

    // If there's only one or no cameras, don't render anything
    if (cameras.length <= 1) return null

    return (
        <div>
            <MediaBtnContainer as="div">
                <button data-tooltip-id="camera-selector" onClick={handleChevronClick}>
                    <FontAwesomeIcon icon={['far', 'video']} fixedWidth />
                </button>
                {type === 'desktop' && (
                    <button id="camera-tooltip" onClick={handleChevronClick}>
                        <FontAwesomeIcon
                            icon={['fas', 'chevron-up']}
                            flip={isOpen ? 'vertical' : undefined}
                            fixedWidth
                        />
                    </button>
                )}
            </MediaBtnContainer>

            <Tip id="camera-selector" content={t('controls.cameraSelectorTooltip')} />

            <Tip
                id="camera-tooltip-ele"
                anchorSelect="#camera-tooltip"
                isOpen={isOpen}
                setIsOpen={() => setIsOpen(prev => !prev)}
                globalCloseEvents={{ escape: true, clickOutsideAnchor: true }}
                imperativeModeOnly={true}
                clickable
                role="dialog"
                className="wide-tooltip"
                openOnClick={true}
            >
                <div>
                    <StyledDropdownOptions>
                        <div className="devices-group">
                            <h3 className="label">{t('controls.cameraSelector')}</h3>
                            {cameras.map(device => {
                                const { deviceId, label } = device
                                const isSelected = activeCamera?.deviceId === deviceId
                                return (
                                    <li key={deviceId}>
                                        <button
                                            className={isSelected ? 'active' : ''}
                                            onClick={() => switchCamera(device)}
                                        >
                                            {label}
                                        </button>
                                    </li>
                                )
                            })}
                        </div>
                        <CloseFloatingBtn onClose={() => setIsOpen(false)} />
                    </StyledDropdownOptions>
                </div>
            </Tip>
        </div>
    )
}
